/**
 * Evaluation: turning a pile of logged sessions into plottable series.
 *
 * Pure functions, no React. The input is always the whole `sportLogs` map —
 * filtering to one habit happens here so callers never have to know how the
 * keys are built.
 *
 * A SERIES is one line on the chart: a station/metric pair tracked over time.
 *
 *   { id, label, points: [{ date: 'YYYY-MM-DD', value: number }] }
 *
 * Sessions with nothing recorded for that metric are skipped rather than
 * plotted as zero — a blank day is missing data, not a score of nothing.
 */

import { isFilledValue, sportFieldKey } from './sport.js';

/**
 * How the sets of one session collapse into a single point.
 * Gym work is usually read as a top set (max) or as volume (total); a
 * one-step activity has a single set, so all three agree.
 */
export const AGGREGATIONS = {
  max: { label: 'best', reduce: (nums) => Math.max(...nums) },
  avg: {
    label: 'average',
    reduce: (nums) => nums.reduce((sum, n) => sum + n, 0) / nums.length,
  },
  total: { label: 'total', reduce: (nums) => nums.reduce((sum, n) => sum + n, 0) },
};

export const DEFAULT_AGGREGATION = 'max';

/** Round away floating-point noise from averaging (1.9999999 -> 2). */
function tidy(value) {
  return Math.round(value * 100) / 100;
}

/**
 * Every recorded session of one habit, oldest first.
 * @param {object} habit
 * @param {Record<string, Record<string, string>>} sportLogs
 * @returns {{date: string, values: Record<string, string>}[]}
 */
export function collectSessions(habit, sportLogs = {}) {
  const prefix = `${habit.id}|`;
  return Object.entries(sportLogs)
    .filter(([key]) => key.startsWith(prefix))
    .map(([key, values]) => ({ date: key.slice(prefix.length), values: values || {} }))
    // Date keys are zero-padded, so a plain string sort is chronological.
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * The most recent session logged *before* a given day.
 *
 * For a weekly habit that is last week's workout, which is exactly what you
 * want to see while filling today's in — the session screen shows those
 * numbers as greyed-out placeholders to aim at.
 *
 * Sessions with nothing in them are skipped: an occurrence that was opened and
 * closed again is not a reference point.
 *
 * @param {object} habit
 * @param {Record<string, object>} sportLogs
 * @param {string} beforeDateKey exclusive upper bound, `YYYY-MM-DD`
 * @returns {{date: string, values: Record<string, string>}|null}
 */
export function findPreviousSession(habit, sportLogs = {}, beforeDateKey = '') {
  const sessions = collectSessions(habit, sportLogs);
  for (let index = sessions.length - 1; index >= 0; index -= 1) {
    const session = sessions[index];
    // Keys are zero-padded, so string comparison is chronological.
    if (session.date >= beforeDateKey) continue;
    if (Object.values(session.values).some(isFilledValue)) return session;
  }
  return null;
}

/**
 * One line per station/metric pair.
 * @param {object} habit sport habit
 * @param {Record<string, object>} sportLogs
 * @param {keyof AGGREGATIONS} [aggregation]
 * @returns {{id: string, label: string, points: {date: string, value: number}[]}[]}
 */
export function buildSeries(habit, sportLogs = {}, aggregation = DEFAULT_AGGREGATION) {
  const sport = habit?.sport;
  if (!sport) return [];

  const reduce = (AGGREGATIONS[aggregation] || AGGREGATIONS[DEFAULT_AGGREGATION]).reduce;
  const sessions = collectSessions(habit, sportLogs);
  const single = sport.stations.length === 1;

  const series = [];
  for (const station of sport.stations) {
    for (const metric of station.metrics) {
      const points = [];

      for (const session of sessions) {
        // Gather this metric across every set of the session.
        const numbers = [];
        for (let setIndex = 0; setIndex < station.sets; setIndex += 1) {
          const raw = session.values[sportFieldKey(station.id, setIndex, metric.id)];
          if (isFilledValue(raw)) numbers.push(Number(raw));
        }
        if (numbers.length > 0) points.push({ date: session.date, value: tidy(reduce(numbers)) });
      }

      series.push({
        id: `${station.id}:${metric.id}`,
        // Only prefix with the station when there is more than one to tell apart.
        label: single || !station.name ? metric.name : `${station.name} · ${metric.name}`,
        points,
      });
    }
  }
  return series;
}

/** True when there is nothing at all to draw. */
export function isSeriesEmpty(series) {
  return series.every((line) => line.points.length === 0);
}

/**
 * Trend of one line: how the last value compares with the first.
 * @param {{points: {value: number}[]}} line
 * @returns {{delta: number, percent: number}|null} null with fewer than 2 points
 */
export function seriesTrend(line) {
  const { points } = line;
  if (points.length < 2) return null;
  const first = points[0].value;
  const last = points[points.length - 1].value;
  return {
    delta: tidy(last - first),
    // A baseline of zero has no meaningful percentage to report.
    percent: first === 0 ? 0 : tidy(((last - first) / Math.abs(first)) * 100),
  };
}
